/**
 * Shared Framer Motion variants and transitions for the marketing site.
 * All marketing components import from here to maintain consistent animation style.
 */

import type { Variants, Transition } from 'framer-motion';

// Standard spring — used for entrance animations (cards, hero elements)
export const mktgSpring: Transition = {
  type: 'spring',
  stiffness: 100,
  damping: 15,
  mass: 0.8
};

// Standard ease — 500ms for large reveals (sections, panels)
export const mktgEase: Transition = {
  duration: 0.5,
  ease: [0.16, 1, 0.3, 1]
};

// Default transition — 300ms for standard transitions
export const mktgDefault: Transition = {
  duration: 0.3,
  ease: [0.16, 1, 0.3, 1]
};

// Micro-interaction — 200ms for small interactions
export const mktgMicro: Transition = {
  duration: 0.2,
  ease: 'easeOut'
};

// NOTE on hidden transitions: every hidden variant snaps (duration 0).
// Sections server-render VISIBLE (see use-entrance.ts) and are switched to
// hidden right after hydration, pre-paint — the snap guarantees no visible
// fade-out flash.

// Fade up (default scroll reveal)
export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24, transition: { duration: 0 } },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
  }
};

// Fade in — opacity only, no movement
export const fadeIn: Variants = {
  hidden: { opacity: 0, transition: { duration: 0 } },
  visible: { opacity: 1, transition: { duration: 0.4 } }
};

// Fade up with spring physics — hero elements, cards
export const fadeUpSpring: Variants = {
  hidden: { opacity: 0, y: 40, transition: { duration: 0 } },
  visible: { opacity: 1, y: 0, transition: mktgSpring }
};

// Fade in from left — text blocks
export const fadeLeft: Variants = {
  hidden: { opacity: 0, x: -40, transition: { duration: 0 } },
  visible: { opacity: 1, x: 0 }
};

// Fade in from right — images, mockups
export const fadeRight: Variants = {
  hidden: { opacity: 0, x: 40, transition: { duration: 0 } },
  visible: { opacity: 1, x: 0 }
};

// Scale up — cards, stat numbers
export const scaleUp: Variants = {
  hidden: { opacity: 0, scale: 0.92, transition: { duration: 0 } },
  visible: { opacity: 1, scale: 1 }
};

// Stagger container — staggers direct children (80ms between siblings)
export const staggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};

// Hero stagger — word-by-word headline reveal
export const heroStagger: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.2
    }
  }
};

// Config for number count-up springs (useSpring)
export const counterSpring = {
  stiffness: 50,
  damping: 20,
  duration: 1.5
};
